import React from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { t } from 'ttag';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import moment from 'moment';
import * as Yup from 'yup';
import FormField from '../../../components/FormField';
import Loading from '../../../components/Loading';
import { USER_TYPES } from '../../../utils/enums';
import { isIntersection } from '../../../utils/helpers';
import useUser from '../../../auth/hooks';
import FormBox from '../../../components/FormBox';
import useApi from '../../../api';
import useEditForm from '../../../hooks/useEditForm';
import useCreateForm from '../../../hooks/useCreateForm';
import NewForm from '../../../components/NewForm';
import LoadingButton from '../../../components/LoadingButton';

const TABS = {
  info: 0,
  schedule: 1,
};

const TabPanel = ({ value, index, children }) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    id={`wrapped-tabpanel-${index}`}
    aria-labelledby={`wrapped-tab-${index}`}
  >
    {value === index && <Box p={3}>{children}</Box>}
  </div>
);

const BaseInfo = ({ data, errors, change, user, api, edit }) => {
  return (
    <>
      <Grid container>
        <Grid item sm={12}>
          <FormBox title={t`Основна інформація`}>
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Назва екзамену`}
                id='name'
                name='name'
                autoComplete='name'
                variant='outlined'
                value={data.name}
                errors={errors.name}
                onChange={change}
              />
            )}
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                query={api.schema.common.sections()[0]}
                preInit={edit}
                dataKey='sections'
                variables={{}}
                type='remoteSelect'
                margin='normal'
                label={t`Оберіть вид спорту`}
                id='section'
                name='section'
                variant='outlined'
                value={data.section}
                errors={errors.section}
                labelKey='name'
                onChange={change}
              />
            )}
            {user.isAdmin && (
              <FormField
                required
                fullWidth
                query={api.schema.clubs.many()[0]}
                preInit={edit}
                dataKey='clubs'
                variables={{}}
                type='remoteSelect'
                margin='normal'
                label={t`Оберіть клуб`}
                id='clubId'
                name='clubId'
                variant='outlined'
                value={data.clubId}
                errors={errors.clubId}
                labelKey='fullName'
                onChange={change}
              />
            )}
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                dataKey='examiners'
                type='text'
                margin='normal'
                label={t`Екзаменатори`}
                id='examiners'
                name='examiners'
                variant='outlined'
                value={data.examiners}
                errors={errors.examiners}
                labelKey='examiners'
                onChange={change}
              />
            )}
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                type='date'
                margin='normal'
                label={t`Дата початку екзамену`}
                id='startedAt'
                name='startedAt'
                variant='outlined'
                value={data.startedAt}
                errors={errors.startedAt}
                onChange={change}
              />
            )}
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                type='date'
                margin='normal'
                minDate={data.startedAt}
                label={t`Дата закінчення екзамену`}
                id='closedAt'
                name='closedAt'
                variant='outlined'
                value={data.closedAt}
                errors={errors.closedAt}
                onChange={change}
              />
            )}
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Місце проведення`}
                id='venue'
                name='venue'
                autoComplete='venue'
                variant='outlined'
                value={data.venue}
                errors={errors.venue}
                onChange={change}
              />
            )}
          </FormBox>
        </Grid>
      </Grid>
    </>
  );
};

const BaseForm = ({ initial, edit, onSubmit, loading }) => {
  const api = useApi();
  const { user } = useUser();
  const [tab, setSab] = React.useState(TABS.info);
  const validationSchema = Yup.object({
    name: Yup.string().required(),
    section: Yup.number().nullable().required(),
  });
  return (
    <NewForm
      onSubmit={onSubmit}
      onError={() => {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
        setSab(TABS.info);
      }}
      initial={initial}
      validationSchema={validationSchema}
    >
      {({ data, change, errors, submit }) => (
        <>
          <Tabs
            value={tab}
            indicatorColor='primary'
            textColor='primary'
            onChange={(e, v) => setSab(v)}
          >
            <Tab label={t`Основна Інформація`} />
          </Tabs>
          <TabPanel value={tab} index={TABS.info}>
            <BaseInfo
              data={data}
              errors={errors}
              change={change}
              user={user}
              api={api}
              edit={edit}
            />
          </TabPanel>
          <TabPanel value={tab} index={TABS.schedule} />
          <LoadingButton
            loading={loading}
            style={{ margin: '20px 0 20px 0' }}
            margin='normal'
            type='button'
            fullWidth
            variant='contained'
            color='primary'
            onClick={submit}
          >
            {t`Зберегти`}
          </LoadingButton>
        </>
      )}
    </NewForm>
  );
};

const getInitialData = (row) => ({
  id: row.id || null,
  name: row.name || '',
  section: row.section || null,
  clubId: row.clubId || null,
  examiners: row.examiners || '',
  startedAt: row.startedAt || null,
  closedAt: row.closedAt || null,
  venue: row.venue || '',
});

const getPostData = (row) => ({
  name: row.name || '',
  section: row.section || null,
  clubId: row.clubId || null,
  examiners: row.examiners || '',
  startedAt:
    (row.startedAt && moment(row.startedAt).format('YYYY-MM-DD')) || null,
  closedAt: (row.closedAt && moment(row.closedAt).format('YYYY-MM-DD')) || null,
  venue: row.venue || '',
});

export const EditForm = () => {
  const api = useApi();
  const { loading, data, onSubmit, submitLoading, errors } = useEditForm({
    schema: api.schema.certifications,
    router: api.router.certifications,
  });

  if (loading) return <Loading />;

  return (
    <>
      <h2>{t`Редагувати екзамен: ${data.name}`}</h2>
      <Paper>
        <Box m={4}>
          <BaseForm
            loading={submitLoading}
            initial={getInitialData(data)}
            onSubmit={(formData) => onSubmit(getPostData(formData))}
            errors={errors}
            edit
          />
        </Box>
      </Paper>
    </>
  );
};

export const CreateForm = () => {
  const { user } = useUser();
  let trainerId;
  if (
    isIntersection(user.types, [USER_TYPES.superManager, USER_TYPES.manager])
  ) {
    trainerId = undefined;
  } else if (isIntersection(user.types, [USER_TYPES.trainer])) {
    trainerId = user.id;
  }
  const api = useApi();
  const { loading, onSubmit, submitLoading, errors } = useCreateForm({
    schema: api.schema.certifications,
    router: api.router.certifications,
  });

  if (loading) return <Loading />;

  return (
    <>
      <h2>{t`Додати екзамен`}</h2>
      <Paper>
        <Box m={4}>
          <BaseForm
            loading={submitLoading}
            initial={getInitialData({
              trainerId,
            })}
            onSubmit={(formData) => onSubmit(getPostData(formData))}
            errors={errors}
            edit={false}
          />
        </Box>
      </Paper>
    </>
  );
};
